/**
 * Etiket şablonu kalıcılık servisi — backend (`LabelTemplateController`).
 *
 * Şablonlar başlangıçta tarayıcı deposunda tutuluyordu; artık veritabanında.
 * Eski cihazlarda kalmış şablonların kaybolmaması için ilk listeleme sırasında
 * tek seferlik taşıma yapılır (bkz. migrateLocalTemplates).
 */

import { accountingApi } from '../config/apiClient';
import { ACCOUNTING_API } from '../config/api';
import {
  LabelTemplate,
  LabelDataSourceKind,
  mapTemplateFromApi,
  mapTemplateToApi,
} from '../types/labelTemplate.types';

const STORAGE_PREFIX = 'gts_label_templates';
const MIGRATION_FLAG_PREFIX = 'gts_label_templates_migrated';

function storageKey(companyId: string): string {
  return `${STORAGE_PREFIX}_${companyId || 'default'}`;
}

function migrationFlagKey(companyId: string): string {
  return `${MIGRATION_FLAG_PREFIX}_${companyId || 'default'}`;
}

/** Backend zarfı: { success, data } */
function unwrap<T>(payload: any): T | null {
  if (!payload) return null;
  if (payload.success === false) {
    throw new Error(payload.message || 'İşlem başarısız');
  }
  return (payload.data ?? payload.Data ?? null) as T | null;
}

function sortTemplates(list: LabelTemplate[]): LabelTemplate[] {
  return [...list].sort((a, b) => {
    if (!!b.isDefault !== !!a.isDefault) return b.isDefault ? 1 : -1;
    return (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '');
  });
}

async function saveTemplate(template: LabelTemplate): Promise<LabelTemplate> {
  const body = mapTemplateToApi(template);
  // Yeni kayıtta Id gönderilmemeli; backend boş Guid'i güncelleme sanmasın
  if (!template.id) delete (body as Record<string, unknown>).Id;

  const res = await accountingApi.post(ACCOUNTING_API.LABEL_TEMPLATE_SAVE, body);
  const dto = unwrap<any>(res.data);
  if (!dto) throw new Error('Şablon kaydedilemedi');
  return mapTemplateFromApi(dto);
}

/**
 * localStorage'da kalmış şablonları bir kez backend'e taşır.
 *
 * Yerel kayıtlar silinmiyor: taşıma yarıda kalırsa veya kullanıcı eski bir
 * sürüme dönerse tasarımlar hâlâ elde olsun. Tekrar taşımayı önleyen bayrak
 * ayrı bir anahtarda tutuluyor.
 */
async function migrateLocalTemplates(companyId: string): Promise<LabelTemplate[]> {
  let local: LabelTemplate[] = [];
  try {
    if (localStorage.getItem(migrationFlagKey(companyId))) return [];
    const raw = localStorage.getItem(storageKey(companyId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return [];
    local = parsed.map((dto) => mapTemplateFromApi(dto));
  } catch {
    return [];
  }

  const migrated: LabelTemplate[] = [];
  for (const template of local) {
    try {
      // Yerel kimlikler ("tpl-...") Guid değil; backend yeni kimlik üretsin
      migrated.push(await saveTemplate({ ...template, id: undefined }));
    } catch {
      // Tek şablon taşınamazsa diğerleri denenmeye devam etsin
    }
  }

  try {
    if (migrated.length === local.length) {
      localStorage.setItem(migrationFlagKey(companyId), new Date().toISOString());
    }
  } catch {
    /* depolama kapalı olabilir */
  }

  return migrated;
}

export const labelTemplateService = {
  async list(companyId: string, dataSource?: LabelDataSourceKind): Promise<LabelTemplate[]> {
    const res = await accountingApi.get(ACCOUNTING_API.LABEL_TEMPLATE_LIST, {
      params: dataSource ? { dataSource } : undefined,
    });
    const data = unwrap<any[]>(res.data) ?? [];
    let templates = data.map((dto) => mapTemplateFromApi(dto));

    // Sunucuda hiç şablon yoksa eski cihazda kalmış olabilir
    if (templates.length === 0) {
      const migrated = await migrateLocalTemplates(companyId);
      if (migrated.length > 0) {
        templates = dataSource ? migrated.filter((t) => t.dataSource === dataSource) : migrated;
      }
    }

    return sortTemplates(templates);
  },

  async getById(_companyId: string, id: string): Promise<LabelTemplate | null> {
    try {
      const res = await accountingApi.get(ACCOUNTING_API.LABEL_TEMPLATE_BY_ID(id));
      const dto = unwrap<any>(res.data);
      return dto ? mapTemplateFromApi(dto) : null;
    } catch {
      return null;
    }
  },

  async save(_companyId: string, template: LabelTemplate): Promise<LabelTemplate> {
    return saveTemplate(template);
  },

  async remove(_companyId: string, id: string): Promise<void> {
    const res = await accountingApi.post(ACCOUNTING_API.LABEL_TEMPLATE_DELETE, { Id: id });
    if (res.data?.success === false) {
      throw new Error(res.data.message || 'Şablon silinemedi');
    }
  },

  async setDefault(_companyId: string, id: string): Promise<void> {
    const res = await accountingApi.post(ACCOUNTING_API.LABEL_TEMPLATE_SET_DEFAULT(id));
    if (res.data?.success === false) {
      throw new Error(res.data.message || 'Varsayılan şablon ayarlanamadı');
    }
  },

  async duplicate(companyId: string, id: string): Promise<LabelTemplate | null> {
    const source = await this.getById(companyId, id);
    if (!source) return null;
    return saveTemplate({
      ...source,
      id: undefined,
      name: `${source.name} (kopya)`,
      isDefault: false,
      createdAt: undefined,
    });
  },

  /** Şablonu dosya olarak dışa aktarma — şubeler arası taşımak için */
  exportToJson(template: LabelTemplate): string {
    return JSON.stringify(template, null, 2);
  },

  importFromJson(json: string): LabelTemplate {
    const parsed = JSON.parse(json) as LabelTemplate;
    if (!parsed || !Array.isArray(parsed.elements)) {
      throw new Error('Geçersiz şablon dosyası');
    }
    return { ...parsed, id: undefined, isDefault: false, createdAt: undefined, updatedAt: undefined };
  },
};

export default labelTemplateService;
